import { HAZARD_LABEL_STRIPE_COUNT, HazardLabelBackground } from "./hazardClassDiamond";

/**
 * The shapes and colours a hazard label is drawn from, as plain data.
 *
 * The PDF draws with react-pdf primitives and Oak's preview draws with DOM
 * ones, but both draw SVG, and an SVG path string means the same thing to
 * each. Keeping the geometry here rather than in either renderer is what
 * stops the two drifting: the corner radius, the inner border and the stripe
 * run all have one definition, and a correction lands in both places at once.
 *
 * Why the shapes are what they are -- the clip path in final orientation, the
 * corner curve that falls short of its control point -- is in
 * docs/waste/dot-label-rendering.md.
 */

const WHITE = "#FFFFFF";

/**
 * The design's diamond as an SVG path, with its points rounded by `radius`.
 *
 * A path in final orientation, NOT a rotated square: rotating one turns
 * class 8's horizontal split into a diagonal bar and class 4.1's vertical
 * stripes into diagonal ones.
 *
 * Each point is rounded by pulling back along both edges that meet there and
 * bridging them with a quadratic curve through the point itself. A quadratic
 * curve never reaches its control point, so a large radius pulls the drawn
 * shape inside its nominal span. `diamondDrawnSpan` says by how much, for a
 * caller that has to hit a measured size.
 */
export function diamondPath(size: number, radius: number, offset = 0): string {
  const mid = size / 2 + offset;
  const inset = 1 + offset;
  const top: [number, number] = [mid, inset];
  const right: [number, number] = [size - 1 + offset, mid];
  const bottom: [number, number] = [mid, size - 1 + offset];
  const left: [number, number] = [inset, mid];
  const corners: Array<[number, number]> = [top, right, bottom, left];
  // Pull-back distance along an edge, as a fraction of that edge's length.
  const edge = Math.hypot(mid - inset, mid - inset);
  const t = Math.min(radius, edge * 0.5) / edge;
  const lerp = (a: [number, number], b: [number, number], k: number): [number, number] => [
    a[0] + (b[0] - a[0]) * k,
    a[1] + (b[1] - a[1]) * k,
  ];
  const parts: string[] = [];
  corners.forEach((corner, index) => {
    const prev = corners[(index + 3) % 4];
    const next = corners[(index + 1) % 4];
    const enter = lerp(corner, prev, t);
    const exit = lerp(corner, next, t);
    parts.push(
      `${index === 0 ? "M" : "L"} ${enter[0]} ${enter[1]}`,
      `Q ${corner[0]} ${corner[1]} ${exit[0]} ${exit[1]}`
    );
  });
  parts.push("Z");
  return parts.join(" ");
}

/**
 * The vertical stripes of a class 4.1 design, across the diamond's full
 * width. Stripes and gaps alternate, so the run is 2n-1 units wide. Clipped
 * to the diamond by the caller.
 */
export function stripePaths(size: number): string[] {
  const stripeWidth = size / (HAZARD_LABEL_STRIPE_COUNT * 2 - 1);
  return Array.from({ length: HAZARD_LABEL_STRIPE_COUNT }, (_unused, index) => {
    const x = index * stripeWidth * 2;
    return `M ${x} 0 h ${stripeWidth} v ${size} h ${-stripeWidth} Z`;
  });
}

/**
 * Whether any part of this background is white, and so needs the diamond
 * outlined to have a visible edge on a white label.
 */
export function backgroundHasWhite(background: HazardLabelBackground): boolean {
  const isWhite = (color: string): boolean => color.toUpperCase() === WHITE;
  if (background.kind === "SOLID") return isWhite(background.color);
  if (background.kind === "SPLIT") {
    return isWhite(background.topColor) || isWhite(background.bottomColor);
  }
  return isWhite(background.groundColor) || isWhite(background.stripeColor);
}

/**
 * Black on a light ground, white on a dark one. Relative luminance, so a new
 * design colour needs no list kept in step with it.
 */
export function borderColorOver(background: string): string {
  const hex = background.replace("#", "");
  const channel = (at: number): number => parseInt(hex.slice(at, at + 2), 16) / 255;
  const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
  return luminance > 0.55 ? "#000000" : "#FFFFFF";
}

/** One run of the inner border: which half it covers, and its colour. */
export interface InnerBorderSegment {
  key: string;
  clip?: "top" | "bottom";
  color: string;
}

/**
 * Which colours the inner border takes, and over which half. One entry for a
 * background that is one colour where the border runs; two for a split,
 * because a single colour disappears into one half -- class 8 is white over
 * black.
 */
export function innerBorderSegments(background: HazardLabelBackground): InnerBorderSegment[] {
  if (background.kind === "SPLIT") {
    return [
      { key: "top", clip: "top", color: borderColorOver(background.topColor) },
      { key: "bottom", clip: "bottom", color: borderColorOver(background.bottomColor) },
    ];
  }
  // A striped ground carries the border over both the stripe and the gap, so
  // it follows the ground the stripes sit on.
  const color = background.kind === "SOLID" ? background.color : background.groundColor;
  return [{ key: "all", color: borderColorOver(color) }];
}

/**
 * The span a path from `diamondPath` actually draws, point to point, for a
 * given nominal size and corner radius.
 *
 * The rounded points are quadratic curves through their corner, and such a
 * curve never reaches its control point. A caller that has to measure 100mm
 * per side asks this rather than assuming the nominal size prints.
 */
export function diamondDrawnSpan(size: number, radius: number): number {
  const mid = size / 2;
  const edge = Math.hypot(mid - 1, mid - 1);
  const pullBack = Math.min(radius, edge * 0.5);
  // A quadratic's extreme is at t = 0.5, where it sits a quarter of the way
  // from the control point to the chord: a quarter of the pull-back along
  // each of the two edges, which is pullBack / (2 * sqrt(2)) along the axis.
  // Both points pull in, so the span loses twice that.
  const shortfall = pullBack / Math.SQRT2;
  // The path insets its points 1 unit from the box on every side.
  return size - 2 - shortfall;
}
